import { useSyncExternalStore } from 'react';
import facts from './planPricing.json';
import type { PlanComparison } from './hooks/usePlanComparison';

export type PlanTier = 'free' | 'pro' | 'ultra';
export type PlanPrice = { monthly: number; yearlyMonthly?: number };
export type PlanPrices = Record<PlanTier, PlanPrice>;

const TIERS: PlanTier[] = ['free', 'pro', 'ultra'];

// Clerk is the source of truth; planPricing.json only until it answers. The
// placeholders match services/planPricing.js, which fills them on the server.
let prices = facts.prices as PlanPrices;
let storage = facts.storage as Record<PlanTier, string>;
const listeners = new Set<() => void>();

export const getPlanPrices = () => prices;

export const planVars = () => {
  const vars: Record<string, string> = {};
  for (const tier of TIERS) {
    vars[`${tier}Storage`] = storage[tier];
    if (tier === 'free') continue;
    const monthly = prices[tier].monthly.toFixed(2);
    const yearly = (prices[tier].yearlyMonthly ?? prices[tier].monthly).toFixed(2);
    vars[`${tier}Monthly`] = monthly;
    vars[`${tier}MonthlyComma`] = monthly.replace('.', ',');
    vars[`${tier}Yearly`] = yearly;
    vars[`${tier}YearlyComma`] = yearly.replace('.', ',');
  }
  return vars;
};

const apply = (comparison: PlanComparison) => {
  const nextPrices = { ...prices };
  const nextStorage = { ...storage };
  const storageRow = comparison.rows.find(r => r.key === 'value:storage');
  for (const tier of TIERS) {
    const plan = comparison.plans.find(p => p.slug === tier);
    if (plan) nextPrices[tier] = { monthly: plan.monthly, yearlyMonthly: plan.yearlyMonthly || undefined };
    const value = storageRow?.values[tier];
    if (typeof value === 'string') nextStorage[tier] = value;
  }
  if (JSON.stringify([nextPrices, nextStorage]) === JSON.stringify([prices, storage])) return;
  prices = nextPrices;
  storage = nextStorage;
  listeners.forEach(listener => listener());
};

/** "Which plan includes it" for a feature page; same rules as featurePagePlans in services/planPricing.js. */
export const featurePagePlans = (
  page: { tier: PlanTier; clerkFeatures?: string[] },
  comparison: PlanComparison | null,
) => {
  const rows = (comparison?.rows || []).filter(r => page.clerkFeatures?.includes(r.key));
  const plans = comparison?.plans || TIERS.map(slug => ({ slug, name: '', monthly: prices[slug].monthly, yearlyMonthly: prices[slug].yearlyMonthly ?? null }));
  return plans.map((plan, rank) => {
    const features = rows.filter(r => r.values[plan.slug]).map(r => (r.kind === 'value' ? `${r.label}: ${r.values[plan.slug]}` : r.label));
    return { ...plan, features, included: rows.length ? features.length > 0 : rank >= TIERS.indexOf(page.tier as PlanTier) };
  });
};

/** Features a plan card shows before its expand button. */
export const VISIBLE_PLAN_FEATURES = 5;

/**
 * Everything a plan includes, inherited features too, as "Label" or "Label: value".
 * What the plan adds over the next cheaper one comes first.
 */
export const planFeatureLabels = (comparison: PlanComparison | null, slug: string) => {
  const plans = comparison?.plans || [];
  const cheaper = plans[plans.findIndex(p => p.slug === slug) - 1]?.slug;
  const rows = (comparison?.rows || []).filter(r => r.values[slug]);
  const added = rows.filter(r => !cheaper || r.values[slug] !== r.values[cheaper]);
  const inherited = rows.filter(r => !added.includes(r));
  return [...added, ...inherited].map(r => (r.kind === 'value' ? `${r.label}: ${r.values[slug]}` : r.label));
};

let request: Promise<PlanComparison> | null = null;

/** GET /api/billing/comparison, once per page load unless it failed. */
export const loadPlanComparison = () => {
  request ??= fetch('/api/billing/comparison')
    .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((comparison: PlanComparison) => {
      apply(comparison);
      return comparison;
    })
    .catch(err => {
      request = null;
      throw err;
    });
  return request;
};

export const subscribePlanPricing = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const usePlanPrices = () => useSyncExternalStore(subscribePlanPricing, getPlanPrices);
